import { S3Client } from '@aws-sdk/client-s3';
import { LOCAL_DEV_BROWSER_STORAGE_ORIGIN } from './media-url';

export { LOCAL_DEV_BROWSER_STORAGE_ORIGIN, normalizeLoamifyBrowserMediaUrl } from './media-url';

export type LoamifyS3Env = {
  NODE_ENV?: string;
  S3_ENDPOINT?: string;
  S3_PRESIGN_ENDPOINT?: string;
  S3_REGION?: string;
  S3_BUCKET?: string;
  S3_PUBLIC_URL?: string;
  S3_ACCESS_KEY?: string;
  S3_SECRET_KEY?: string;
  S3_QUARANTINE_ENDPOINT?: string;
  S3_QUARANTINE_PRESIGN_ENDPOINT?: string;
  S3_QUARANTINE_BUCKET?: string;
  S3_QUARANTINE_ACCESS_KEY?: string;
  S3_QUARANTINE_SECRET_KEY?: string;
  S3_EXPORT_ENDPOINT?: string;
  S3_EXPORT_PRESIGN_ENDPOINT?: string;
  S3_EXPORT_BUCKET?: string;
  S3_EXPORT_ACCESS_KEY?: string;
  S3_EXPORT_SECRET_KEY?: string;
};

/** Presigned PUT URLs must use a host the browser can reach; internal Docker hosts are not valid. */
export function resolveLoamifyPresignEndpoint(
  internalEndpoint: string,
  configuredPresignEndpoint: string | undefined,
  nodeEnv: string | undefined,
): string {
  const trimmed = configuredPresignEndpoint?.trim();
  if (trimmed) return trimmed;
  if (nodeEnv !== 'production') return LOCAL_DEV_BROWSER_STORAGE_ORIGIN;
  return internalEndpoint;
}

/** Public media URLs are loaded by the browser; local dev should use localhost, not loopback. */
export function resolveLoamifyPublicMediaBaseUrl(
  internalEndpoint: string,
  bucket: string,
  configuredPublicUrl: string | undefined,
  nodeEnv: string | undefined,
): string {
  const trimmed = configuredPublicUrl?.trim();
  if (trimmed) return trimmed.replace(/\/$/, '');
  if (nodeEnv !== 'production') {
    return `${LOCAL_DEV_BROWSER_STORAGE_ORIGIN}/${bucket}`;
  }
  return `${internalEndpoint.replace(/\/$/, '')}/${bucket}`;
}

export type LoamifyS3Clients = {
  /** Internal/object-store client (API/worker ↔ MinIO/R2 inside Docker). */
  s3: S3Client;
  /** Browser-facing presigned URLs — must match the Host the client sends (e.g. CDN). */
  presignS3: S3Client;
  bucket: string;
  publicUrl: string;
  quarantineS3: S3Client;
  quarantinePresignS3: S3Client;
  quarantineBucket: string;
  exportS3: S3Client;
  exportPresignS3: S3Client;
  exportBucket: string;
};

export function createLoamifyS3ClientsFromEnv(
  env: LoamifyS3Env = process.env as LoamifyS3Env,
): LoamifyS3Clients {
  if (
    env.NODE_ENV === 'production' &&
    (!env.S3_ACCESS_KEY?.trim() ||
      !env.S3_SECRET_KEY?.trim() ||
      env.S3_ACCESS_KEY === 'minioadmin' ||
      env.S3_SECRET_KEY === 'minioadmin' ||
      env.S3_ACCESS_KEY.includes('CHANGE_ME') ||
      env.S3_SECRET_KEY.includes('CHANGE_ME'))
  ) {
    throw new Error('Production storage credentials must be explicitly set and non-default');
  }
  const endpoint = env.S3_ENDPOINT ?? 'http://127.0.0.1:9000';
  const presignEndpoint = resolveLoamifyPresignEndpoint(
    endpoint,
    env.S3_PRESIGN_ENDPOINT,
    env.NODE_ENV,
  );
  const region = env.S3_REGION ?? 'us-east-1';
  const bucket = env.S3_BUCKET ?? 'loamify-uploads';
  const publicUrl = resolveLoamifyPublicMediaBaseUrl(
    endpoint,
    bucket,
    env.S3_PUBLIC_URL,
    env.NODE_ENV,
  );
  const credentials = {
    accessKeyId: env.S3_ACCESS_KEY ?? 'minioadmin',
    secretAccessKey: env.S3_SECRET_KEY ?? 'minioadmin',
  };
  const quarantineEndpoint = env.S3_QUARANTINE_ENDPOINT?.trim() || endpoint;
  const quarantinePresignEndpoint = resolveLoamifyPresignEndpoint(
    quarantineEndpoint,
    env.S3_QUARANTINE_PRESIGN_ENDPOINT,
    env.NODE_ENV,
  );
  const quarantineCredentials = {
    accessKeyId: env.S3_QUARANTINE_ACCESS_KEY?.trim() || credentials.accessKeyId,
    secretAccessKey: env.S3_QUARANTINE_SECRET_KEY?.trim() || credentials.secretAccessKey,
  };
  const exportEndpoint = env.S3_EXPORT_ENDPOINT?.trim() || endpoint;
  const exportPresignEndpoint = resolveLoamifyPresignEndpoint(
    exportEndpoint,
    env.S3_EXPORT_PRESIGN_ENDPOINT,
    env.NODE_ENV,
  );
  const exportCredentials = {
    accessKeyId: env.S3_EXPORT_ACCESS_KEY?.trim() || credentials.accessKeyId,
    secretAccessKey: env.S3_EXPORT_SECRET_KEY?.trim() || credentials.secretAccessKey,
  };
  const base = {
    region,
    forcePathStyle: true as const,
    credentials,
    requestChecksumCalculation: 'WHEN_REQUIRED' as const,
    responseChecksumValidation: 'WHEN_REQUIRED' as const,
  };

  return {
    s3: new S3Client({ ...base, endpoint }),
    presignS3: new S3Client({ ...base, endpoint: presignEndpoint }),
    bucket,
    publicUrl,
    quarantineS3: new S3Client({
      ...base,
      credentials: quarantineCredentials,
      endpoint: quarantineEndpoint,
    }),
    quarantinePresignS3: new S3Client({
      ...base,
      credentials: quarantineCredentials,
      endpoint: quarantinePresignEndpoint,
    }),
    quarantineBucket: env.S3_QUARANTINE_BUCKET ?? 'loamify-quarantine',
    exportS3: new S3Client({
      ...base,
      credentials: exportCredentials,
      endpoint: exportEndpoint,
    }),
    exportPresignS3: new S3Client({
      ...base,
      credentials: exportCredentials,
      endpoint: exportPresignEndpoint,
    }),
    exportBucket: env.S3_EXPORT_BUCKET ?? 'loamify-private-exports',
  };
}

/** Worker/internal use — object store client only (no presign client). */
export function createLoamifyS3ObjectStoreFromEnv(
  env: LoamifyS3Env = process.env as LoamifyS3Env,
): Pick<
  LoamifyS3Clients,
  's3' | 'bucket' | 'publicUrl' | 'quarantineS3' | 'quarantineBucket' | 'exportS3' | 'exportBucket'
> {
  const { s3, bucket, publicUrl, quarantineS3, quarantineBucket, exportS3, exportBucket } =
    createLoamifyS3ClientsFromEnv(env);
  return { s3, bucket, publicUrl, quarantineS3, quarantineBucket, exportS3, exportBucket };
}
